/**
 * Hindi for the cash book owner/manager screens (V1_SPEC section 12, UX_COPY frames 6-9). English is
 * the default for staff; Hindi shows when the Karkhana language is Hindi. FE-2 owns this file after
 * the first cash book commit and adds every further owner string it renders.
 * Left out on purpose: "To check" (HI already says "जाँच में" on other screens; cashbookI18n.test.ts).
 */
export const HI_CASHBOOK_OWNER: Record<string, string> = {
  "Checked history": "जाँच हो चुकी",
  "Type the TOTAL written on the paper": "पत्रक पर लिखा कुल (TOTAL) लिखें",
  "Total matches: {{paid}} paid of {{due}} due.": "कुल मिल गया: {{due}} देय में से {{paid}} दिया।",
  "{{gap}} unexplained. Due minus the rows you changed is {{expected}}, but the paper says {{paper}}. Find the rows that differ before verifying.":
    "{{gap}} का हिसाब नहीं। देय में से बदली पंक्तियाँ घटाकर {{expected}} बनता है, पर पत्रक पर {{paper}} लिखा है। सही करने से पहले अलग पंक्तियाँ ढूँढें।",
  "Explain {{gap}} first": "पहले {{gap}} का हिसाब ढूँढें",
  "Needs a look ({{n}})": "देखना है ({{n}})",
  "Spot checks ({{n}})": "बिना देखे जाँच ({{n}})",
  "Not changed in the app ({{n}})": "ऐप में नहीं बदली ({{n}})",
  "Type what the paper says": "पत्रक पर जो लिखा है वह लिखें",
  Verify: "सही है",
  "Verify these {{n}}": "ये {{n}} सही हैं",
  "You recorded this. Someone else checks it.": "यह आपने दर्ज किया। कोई और जाँचेगा।",
  "Your own pay. Someone else checks it.": "यह आपका अपना भुगतान है। कोई और जाँचेगा।",
  "Verify anyway": "फिर भी सही करें",
  "Approve {{amount}}": "{{amount}} मंज़ूर करें",
  "Keep {{amount}}": "{{amount}} ही रखें",
  "{{name}}'s cash in hand {{delta}}": "{{name}} के हाथ में नकद {{delta}}",
  "{{name}}: {{amount}} more due, goes on the next sheet": "{{name}}: {{amount}} और देय, अगले पत्रक पर जाएगा",
  "The paper's sum is wrong": "पत्रक का जोड़ ग़लत है",
  "Paid twice": "दो बार भुगतान",
  "More than due": "देय से ज़्यादा",
  "Edited {{n}}×, first {{amount}}": "{{n}} बार बदला, पहले {{amount}}",
  "Pay changed after print": "छपने के बाद भुगतान बदला",
  "Photo came after checking started": "जाँच शुरू होने के बाद फ़ोटो आई",
  "Totals differ": "कुल अलग हैं",
  Supervisors: "सुपरवाइज़र",
  "Spend types": "खर्च के प्रकार",
  "Close dispute": "मामला बंद करें",
  "Nothing to check. Every sheet up to yesterday is checked.": "जाँचने को कुछ नहीं। कल तक के सब पत्रक जाँच हो चुके।",
  // FE-2: owner screens (inbox, sheet check, spends check, worker card, history, spend types, handovers)
  "1 sheet": "1 पत्रक",
  "{{n}} sheets": "{{n}} पत्रक",
  "1 spends page": "1 खर्च का पन्ना",
  "{{n}} spends pages": "{{n}} खर्च के पन्ने",
  "1 change to decide": "1 बदलाव तय करना है",
  "{{n}} changes to decide": "{{n}} बदलाव तय करने हैं",
  "1 handover": "1 नकद लेन-देन",
  "{{n}} handovers": "{{n}} नकद लेन-देन",
  "1 item": "1 चीज़",
  "{{n}} items": "{{n}} चीज़ें",
  "1 photo missing": "1 फ़ोटो नहीं आई",
  "{{n}} photos missing": "{{n}} फ़ोटो नहीं आईं",
  "1 other entry": "1 और एंट्री",
  "{{n}} other entries": "{{n}} और एंट्री",
  "Approve: cash in hand {{delta}}": "मंज़ूर करने पर: हाथ में नकद {{delta}}",
  "{{name}} {{amount}} more due": "{{name}} का {{amount}} और देय",
  "{{name}} {{amount}} less due": "{{name}} का {{amount}} कम देय",
  "Change: {{name}} wants to remove {{amount}} for {{person}}{{sheet}}.": "बदलाव: {{name}} {{person}} का {{amount}} हटाना चाहते हैं{{sheet}}।",
  "Change: {{name}} wants to remove {{amount}}{{sheet}}.": "बदलाव: {{name}} {{amount}} हटाना चाहते हैं{{sheet}}।",
  "Change: {{name}} wants {{old}} → {{new}} for {{person}}{{sheet}}.": "बदलाव: {{name}} {{person}} के लिए {{old}} → {{new}} चाहते हैं{{sheet}}।",
  "Change: {{name}} wants {{old}} → {{new}}{{sheet}}.": "बदलाव: {{name}} {{old}} → {{new}} चाहते हैं{{sheet}}।",
  "Removed. {{amount}} stays in the record.": "हटा दिया। {{amount}} रिकॉर्ड में रहेगा।",
  "{{new}} is now in force. {{old}} stays in the record.": "अब {{new}} गिना जाएगा। {{old}} रिकॉर्ड में रहेगा।",
  "{{amount}} stays in force.": "{{amount}} ही गिना जाएगा।",
  "{{name}} asked": "{{name}} ने माँगा",
  "This is your own pay. Someone else decides it.": "यह आपका अपना भुगतान है। कोई और तय करेगा।",
  "{{name}} asked for this change, so someone else decides it.": "यह बदलाव {{name}} ने माँगा है, इसलिए कोई और तय करेगा।",
  "As the owner you may decide anyway; it is marked self-approved.": "मालिक होने के नाते आप फिर भी तय कर सकते हैं; इस पर \"ख़ुद मंज़ूर\" लिखा रहेगा।",
  "e.g. Checked with Mohan": "जैसे: मोहन से पूछा",
  "Approve anyway": "फिर भी मंज़ूर करें",
  "Approve removal": "हटाना मंज़ूर करें",
  "Say why to approve or keep.": "मंज़ूर करने या रखने के लिए कारण लिखें।",
  "change pending": "बदलाव बाकी",
  "remove": "हटाएँ",
  "Nothing checked yet.": "अभी कुछ जाँचा नहीं गया।",
  "Spends page · {{name}} · {{date}}": "खर्च का पन्ना · {{name}} · {{date}}",
  "{{n}} lines": "{{n}} पंक्तियाँ",
  "{{n}} corrected": "{{n}} सुधारी",
  "checked {{when}}": "जाँच {{when}}",
  "by {{names}}": "{{names}} ने",
  "Load older": "पुराने देखें",
  "{{n}} rows": "{{n}} पंक्तियाँ",
  "Supervisor cash": "सुपरवाइज़र का नकद",
  "Type a spend for them": "इनका खर्च लिखें",
  "Missing: {{name}}'s pay sheet {{sheetNo}} (pages {{pages}}).": "नहीं आया: {{name}} का भुगतान पत्रक {{sheetNo}} (पन्ने {{pages}})।",
  "Missing: {{name}}'s pay sheet {{sheetNo}}.": "नहीं आया: {{name}} का भुगतान पत्रक {{sheetNo}}।",
  "Missing: {{name}}'s spends page for {{date}}.": "नहीं आया: {{name}} का {{date}} का खर्च का पन्ना।",
  "Call {{name}}": "{{name}} को फ़ोन करें",
  "Copied": "कॉपी हो गया",
  "Copy": "कॉपी करें",
  "Cash handed to {{name}}": "{{name}} को नकद दिया",
  "{{name}} gave cash back": "{{name}} ने नकद लौटाया",
  "{{from}} → {{to}}": "{{from}} → {{to}}",
  "Other entries ({{n}})": "और एंट्री ({{n}})",
  "No supervisors yet. Add them under Supervisors in Karkhana settings.": "अभी कोई सुपरवाइज़र नहीं। कारख़ाना सेटिंग में सुपरवाइज़र जोड़ें।",
  "Changes to decide": "तय करने वाले बदलाव",
  "Decide": "तय करें",
  "Handovers": "नकद लेन-देन",
  "{{amount}} from {{from}} to {{to}} has been in transit for a day.": "{{from}} से {{to}} को {{amount}} एक दिन से रास्ते में है।",
  "{{to}} said no to {{amount}} from {{from}}.": "{{to}} ने {{from}} के {{amount}} से मना किया।",
  "sent {{time}}": "{{time}} भेजा",
  "{{n}} of {{total}} verified": "{{total}} में से {{n}} सही",
  "pages {{pages}} not sent": "पन्ने {{pages}} नहीं आए",
  "Spends page · {{name}} · {{n}} lines": "खर्च का पन्ना · {{name}} · {{n}} पंक्तियाँ",
  "Open day": "दिन खोलें",
  "Checking speed (30 days)": "जाँच की रफ़्तार (30 दिन)",
  "{{name}}: {{sec}} s a row, corrects {{pct}}% · {{rows}} rows": "{{name}}: {{sec}} सेकंड एक पंक्ति, {{pct}}% सुधार · {{rows}} पंक्तियाँ",
  "Very fast with no corrections can mean rows aren't being looked at.": "बहुत तेज़ और कोई सुधार नहीं, तो शायद पंक्तियाँ देखी नहीं जा रहीं।",
  "{{amount}} cash to one payee today. Over ₹10,000 in cash may not count as a tax expense.": "आज एक ही व्यक्ति को {{amount}} नकद। ₹10,000 से ज़्यादा नकद टैक्स में खर्च नहीं गिना जा सकता।",
  "{{from}} says they gave {{to}} {{amount}} (handover disputed).": "{{from}} कहते हैं उन्होंने {{to}} को {{amount}} दिए (लेन-देन पर विवाद)।",
  "Photo didn't load. Retry": "फ़ोटो नहीं खुली। फिर से कोशिश करें",
  "No photo for this page yet": "इस पन्ने की फ़ोटो अभी नहीं आई",
  "Photo didn't load.": "फ़ोटो नहीं खुली।",
  "Zoom out": "छोटा करें",
  "Zoom in": "बड़ा करें",
  "Sheet check": "पत्रक की जाँच",
  "{{n}} not verified. See the rows marked below.": "{{n}} सही नहीं हुईं। नीचे निशान लगी पंक्तियाँ देखें।",
  "{{sheetNo}} checked. {{n}} rows verified, {{c}} corrected.": "{{sheetNo}} की जाँच हो गई। {{n}} पंक्तियाँ सही, {{c}} सुधारी।",
  "{{name}}: {{amount}} more than due": "{{name}}: देय से {{amount}} ज़्यादा",
  "Back to To check": "जाँच की सूची पर लौटें",
  "Photo {{time}} · Print {{n}} · not edited": "फ़ोटो {{time}} · प्रिंट {{n}} · बदली नहीं गई",
  "Handwritten": "हाथ से लिखा",
  "Page {{page}} not sent yet": "पन्ना {{page}} अभी नहीं आया",
  "Page {{page}} of {{pages}} · rows {{from}}–{{to}}": "पन्ना {{page}} / {{pages}} · पंक्ति {{from}}–{{to}}",
  "Page {{page}} of {{pages}}": "पन्ना {{page}} / {{pages}}",
  "Pay sheet {{sheetNo}}, page {{page}}": "भुगतान पत्रक {{sheetNo}}, पन्ना {{page}}",
  "{{n}} photos of this page; the newest is shown.": "इस पन्ने की {{n}} फ़ोटो; सबसे नई दिख रही है।",
  "due {{amount}}": "देय {{amount}}",
  "The TOTAL PAID box on the last page.": "आख़िरी पन्ने का TOTAL PAID वाला ख़ाना।",
  "by you": "आपने",
  "Due {{amount}}": "देय {{amount}}",
  "Flagged": "निशान लगा",
  "More than due: why?": "देय से ज़्यादा: क्यों?",
  "Keep both: why?": "दोनों रखें: क्यों?",
  "e.g. two different weeks": "जैसे: दो अलग हफ़्ते",
  "This print was replaced by a newer print. Rows can't be checked here.": "इसकी जगह नया प्रिंट बन चुका है। यहाँ पंक्तियाँ नहीं जाँची जा सकतीं।",
  "Paper TOTAL {{amount}}": "पत्रक का TOTAL {{amount}}",
  "You said the paper's sum is wrong. Check each row against the photo.": "आपने कहा पत्रक का जोड़ ग़लत है। हर पंक्ति फ़ोटो से मिलाएँ।",
  "{{n}} rows picked at random to check one at a time.": "{{n}} पंक्तियाँ बिना क्रम के चुनी गईं, एक-एक करके जाँचें।",
  "Photo of each row": "हर पंक्ति की फ़ोटो",
  "Laptop keys: Enter = paid as due · ↑ ↓ move · F = flag · digits type the amount.": "लैपटॉप की कुंजियाँ: Enter = देय जितना दिया · ↑ ↓ चलें · F = निशान · अंक = रकम लिखें।",
  "The gap stops blocking. Every row still needs its own check against the photo.": "अंतर अब नहीं रोकेगा। हर पंक्ति फिर भी फ़ोटो से जाँचनी है।",
  "Already verified": "पहले से सही",
  "Someone else checks this row": "यह पंक्ति कोई और जाँचेगा",
  "Type what the paper says first": "पहले पत्रक पर लिखी रकम लिखें",
  "More than due: pick why first": "देय से ज़्यादा: पहले कारण चुनें",
  "Paid twice: say why both stay": "दो बार भुगतान: दोनों क्यों रहें, लिखें",
  "More than 1.5 times the due. Check the paper.": "देय के डेढ़ गुने से ज़्यादा। पत्रक देखें।",
  "No photo of this page yet": "इस पन्ने की फ़ोटो अभी नहीं आई",
  "No photo of this page. Verify from the paper you hold?": "इस पन्ने की फ़ोटो नहीं है। अपने पास के कागज़ से सही करें?",
  "Photo": "फ़ोटो",
  "Blurry": "धुंधली",
  "Previous page": "पिछला पन्ना",
  "Next page": "अगला पन्ना",
  "Supervisor wrote {{amount}}": "सुपरवाइज़र ने लिखा {{amount}}",
  "Verify your own entry? It stays marked \"self-verified\" in the exceptions report.": "अपनी ही एंट्री सही करें? अपवाद रिपोर्ट में इस पर \"ख़ुद सही की\" लिखा रहेगा।",
  "Leave for someone else": "किसी और के लिए छोड़ें",
  "Save reason": "कारण सेव करें",
  "Verified by {{name}} (self-verified)": "{{name}} ने सही किया (ख़ुद सही की)",
  "Verified by {{name}}": "{{name}} ने सही किया",
  "Row {{n}}, {{name}}": "पंक्ति {{n}}, {{name}}",
  "e.g. Thread": "जैसे: धागा",
  "Hindi name": "हिंदी नाम",
  "Ledger head (Tally, optional)": "लेजर हेड (Tally, ज़रूरी नहीं)",
  "Tally: {{name}}": "Tally: {{name}}",
  "Rename": "नाम बदलें",
  "Retire": "बंद करें",
  "Bring back": "फिर से चालू करें",
  "Supervisors pick these when they type a spend. Renaming or retiring never changes past spends.": "सुपरवाइज़र खर्च लिखते समय इनमें से चुनते हैं। नाम बदलने या बंद करने से पुराने खर्च नहीं बदलते।",
  "Add a spend type": "खर्च का प्रकार जोड़ें",
  "Retired ({{n}})": "बंद किए ({{n}})",
  "That spend type already exists.": "यह खर्च का प्रकार पहले से है।",
  "On the paper but not typed yet.": "पन्ने पर है, पर अभी लिखा नहीं गया।",
  "Add from photo": "फ़ोटो से जोड़ें",
  "Spend type": "खर्च का प्रकार",
  "Choose…": "चुनें…",
  "Paid to": "किसे दिया",
  "Spends page · {{name}} · {{date}} · {{n}} lines": "खर्च का पन्ना · {{name}} · {{date}} · {{n}} पंक्तियाँ",
  "Spends check": "खर्च की जाँच",
  "Photo {{time}} · not edited": "फ़ोटो {{time}} · बदली नहीं गई",
  "Photo {{n}} of {{total}}": "फ़ोटो {{n}} / {{total}}",
  "Spends page of {{name}}": "{{name}} का खर्च का पन्ना",
  "Type the total written on the page": "पन्ने पर लिखा कुल लिखें",
  "Page total {{amount}}": "पन्ने का कुल {{amount}}",
  "typed {{amount}}": "लिखा गया {{amount}}",
  "You said the page's sum is wrong. Check each spend against the photo.": "आपने कहा पन्ने का जोड़ ग़लत है। हर खर्च फ़ोटो से मिलाएँ।",
  "Page total matches the typed spends.": "पन्ने का कुल लिखे गए खर्च से मिल गया।",
  "{{gap}} unexplained. The typed spends add up to {{typed}}, but the page says {{paper}}.": "{{gap}} का हिसाब नहीं। लिखे गए खर्च {{typed}} बनते हैं, पर पन्ने पर {{paper}} लिखा है।",
  "The page's sum is wrong": "पन्ने का जोड़ ग़लत है",
  "to {{name}}": "{{name}} को",
  "Edited {{n}}×": "{{n}} बार बदला",
  "Paid in the app": "ऐप में भुगतान",
  "Verified": "सही",
  "Not checked yet": "अभी जाँच नहीं हुई",
  "Worker payments": "कारीगर के भुगतान",
  "No cash book payments for {{name}} yet.": "{{name}} का अभी कोई नकद भुगतान नहीं।",
  "Older payments": "पुराने भुगतान",
  "The worker": "कारीगर",
  "{{name}}: {{amount}} less due": "{{name}}: {{amount}} कम देय",
  "from {{sheetNo}} photo": "{{sheetNo}} की फ़ोटो से",
  "through the assistant": "असिस्टेंट से",
  "{{amount}} before the cash book started": "नकद खाता शुरू होने से पहले {{amount}}",
  "Not paid (left blank)": "भुगतान नहीं (ख़ाली छोड़ा)",
  "{{amount}} paid": "{{amount}} दिया",
  "{{name}} changed {{before}} → {{after}}": "{{name}} ने {{before}} → {{after}} किया",
  "{{name}} asked to remove {{before}}": "{{name}} ने {{before}} हटाने को कहा",
  "{{name}} asked to change {{before}} → {{after}}": "{{name}} ने {{before}} → {{after}} बदलने को कहा",
  "{{name}} approved the removal": "{{name}} ने हटाना मंज़ूर किया",
  "{{name}} approved {{after}}": "{{name}} ने {{after}} मंज़ूर किया",
  "self-approved": "ख़ुद मंज़ूर",
  "{{name}} kept {{amount}}": "{{name}} ने {{amount}} ही रखा",
  "{{name}} removed it": "{{name}} ने हटाया",
  "{{name}} gave {{amount}}": "{{name}} ने {{amount}} दिए",
  "{{name}} said received": "{{name}} ने मिल गए दबाया",
  "{{name}} said it's wrong": "{{name}} ने कहा ग़लत है",
  "{{name}} gave again: {{amount}}": "{{name}} ने फिर से दिए: {{amount}}",
  "{{name}} told the owner": "{{name}} ने मालिक को बताया",
  "{{name}} closed the dispute": "{{name}} ने मामला बंद किया",
  "Spot check": "बिना देखे जाँच",
  "You recorded this": "यह आपने दर्ज किया",
  "Your own pay": "आपका अपना भुगतान",
  "Verify this row on its own.": "इस पंक्ति को अलग से सही करें।",
  "A newer photo of this page came in. Look at it before verifying.": "इस पन्ने की नई फ़ोटो आई है। सही करने से पहले उसे देखें।",
  "This change was already decided.": "यह बदलाव पहले ही तय हो चुका।",
  "Say why.": "कारण लिखें।",
  "More than due: pick why (advance, bonus, back pay or wrong).": "देय से ज़्यादा: कारण चुनें (एडवांस, बोनस, पिछला बकाया या ग़लत)।",
  "This print was replaced by a newer print. Check the new one.": "इसकी जगह नया प्रिंट बन चुका है। नया वाला जाँचें।",
  "At most 10 at a time.": "एक बार में ज़्यादा से ज़्यादा 10।",
  "This handover was already answered.": "इस लेन-देन का जवाब पहले ही दिया जा चुका।",
  "Bonus": "बोनस",
  "Back pay": "पिछला बकाया",
  "Wrong": "ग़लत",
  "Explain the gap first": "पहले अंतर का हिसाब ढूँढें",
  "What did you find?": "क्या पता चला?",
  "e.g. Talked to both; Suresh found ₹500": "जैसे: दोनों से बात की; सुरेश को ₹500 मिले",
  "{{name}} paid {{amount}} by hand on {{date}} (1 entry), no sheet photo.": "{{name}} ने {{date}} को {{amount}} हाथ से दिए (1 एंट्री), पत्रक की फ़ोटो नहीं।",
  "{{name}} paid {{amount}} by hand on {{date}} ({{n}} entries), no sheet photo.": "{{name}} ने {{date}} को {{amount}} हाथ से दिए ({{n}} एंट्री), पत्रक की फ़ोटो नहीं।",
  "Mark reason": "कारण लिखें",
  "Why is there no photo?": "फ़ोटो क्यों नहीं है?",
  "e.g. Printer broken, paid from the register": "जैसे: प्रिंटर ख़राब था, रजिस्टर से भुगतान किया",
  "The reason is kept with your name and can't be changed.": "कारण आपके नाम के साथ रहेगा, बदला नहीं जा सकता।",
  "Paid by hand, no sheet photo. Reason by {{name}}: {{reason}}": "हाथ से भुगतान, पत्रक की फ़ोटो नहीं। {{name}} का कारण: {{reason}}",
};
